import React, { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import useActions, { useAppSelector } from "../../../app/hooks";
import { selectProjectTasks } from "../../library/librarySlice";
import { Project } from "../../../types/Project";
import { ProjectTask } from "../../../types/ProjectTask";
import TaskBreakdownModal from "./TaskBreakdownModal";

export default function ProjectModal(props: {
  open: boolean;
  project: Project | null;
  onClose: () => void;
}) {
  const actions = useActions();
  const tasks = useAppSelector(selectProjectTasks);
  const [createMode, setCreateMode] = useState(false);
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState("");
  const [prompt, setPrompt] = useState("");
  const [promptError, setPromptError] = useState("");
  const [taskEdits, setTaskEdits] = useState<
    Record<number, { name: string; prompt: string; go_background: boolean }>
  >({});
  const [editingTasks, setEditingTasks] = useState<Record<number, boolean>>({});
  const [viewTask, setViewTask] = useState<ProjectTask | null>(null);

  useEffect(() => {
    if (props.open && props.project) {
      actions.getProjectTasks(props.project.id);
    }
  }, [props.open, props.project?.id]);

  useEffect(() => {
    const initial: Record<
      number,
      { name: string; prompt: string; go_background: boolean }
    > = {};
    tasks.forEach((t) => {
      initial[t.id] = {
        name: t.name,
        prompt: t.prompt ?? "",
        go_background: !!t.go_background,
      };
    });
    setTaskEdits(initial);
  }, [tasks]);

  function resetForm() {
    setCreateMode(false);
    setName("");
    setNameError("");
    setPrompt("");
    setPromptError("");
  }

  function handleNameChange(e: React.ChangeEvent<HTMLInputElement>) {
    const v = e.target.value;
    setName(v);
    setNameError(v.trim() === "" ? "Name is required" : "");
  }

  function handlePromptChange(e: React.ChangeEvent<HTMLInputElement>) {
    const v = e.target.value;
    setPrompt(v);
    setPromptError(v.trim() === "" ? "Prompt is required" : "");
  }

  function handleTaskFieldChange(
    taskId: number,
    field: "name" | "prompt",
    value: string
  ) {
    setTaskEdits((prev) => ({
      ...prev,
      [taskId]: {
        name: prev[taskId]?.name ?? "",
        prompt: prev[taskId]?.prompt ?? "",
        go_background: prev[taskId]?.go_background ?? false,
        [field]: value,
      },
    }));
  }

  function handleTaskGoBackgroundChange(taskId: number, value: boolean) {
    setTaskEdits((prev) => ({
      ...prev,
      [taskId]: {
        name: prev[taskId]?.name ?? "",
        prompt: prev[taskId]?.prompt ?? "",
        go_background: value,
      },
    }));
  }

  function handleSaveTask(taskId: number) {
    if (!props.project) return;
    const edit = taskEdits[taskId];
    if (!edit) return;
    actions.updateProjectTask({
      projectId: props.project.id,
      taskId,
      projectTaskUpdateRequest: {
        name: edit.name.trim(),
        prompt: edit.prompt.trim(),
        go_background: edit.go_background,
      },
    });
    setEditingTasks((prev) => ({ ...prev, [taskId]: false }));
  }

  function handleStartEditTask(taskId: number) {
    setEditingTasks((prev) => ({ ...prev, [taskId]: true }));
  }

  function handleCreateTask() {
    if (!props.project) return;
    const n = name.trim();
    const p = prompt.trim();
    if (n === "") {
      setNameError("Name is required");
      return;
    }
    if (p === "") {
      setPromptError("Prompt is required");
      return;
    }
    actions.createProjectTask({
      projectId: props.project.id,
      projectTaskCreateRequest: { name: n, prompt: p },
    });
    resetForm();
  }

  if (!props.project) return null;

  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Tasks: {props.project.name}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          {!createMode && (
            <>
              {tasks.length === 0 && (
                <Typography sx={{ mb: 2, textAlign: "center" }}>
                  No tasks assigned to this project.
                </Typography>
              )}
              <List>
                {tasks.map((task) => {
                  const edit = taskEdits[task.id] ?? {
                    name: task.name,
                    prompt: task.prompt ?? "",
                    go_background: !!task.go_background,
                  };
                  const isEditing = !!editingTasks[task.id];

                  if (!isEditing) {
                    const secondaryLines = [];
                    if (task.prompt) secondaryLines.push(task.prompt);
                    if (task.go_background) secondaryLines.push("Go background");

                    return (
                      <ListItem
                        key={"task_" + task.id}
                        sx={{
                          py: 1,
                          alignItems: "center",
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 1,
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="subtitle2" noWrap>
                            {task.name}
                          </Typography>
                          {secondaryLines.length > 0 && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              noWrap
                            >
                              {secondaryLines.join(" • ")}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Button
                            variant="text"
                            size="small"
                            onClick={() => setViewTask(task)}
                          >
                            View
                          </Button>
                          <IconButton
                            size="small"
                            onClick={() => handleStartEditTask(task.id)}
                            aria-label="Edit task"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </ListItem>
                    );
                  }

                  return (
                    <ListItem
                      key={"task_" + task.id}
                      sx={{
                        py: 1,
                        alignItems: "flex-start",
                        flexDirection: "column",
                      }}
                    >
                      <Box sx={{ width: "100%", mb: 1 }}>
                        <TextField
                          label="Task name"
                          fullWidth
                          variant="standard"
                          value={edit.name}
                          onChange={(e) =>
                            handleTaskFieldChange(task.id, "name", e.target.value)
                          }
                          sx={{ mb: 1 }}
                        />
                        <TextField
                          label="Prompt"
                          fullWidth
                          variant="standard"
                          multiline
                          minRows={2}
                          value={edit.prompt}
                          onChange={(e) =>
                            handleTaskFieldChange(task.id, "prompt", e.target.value)
                          }
                          sx={{ mb: 1 }}
                        />
                      </Box>
                      <Box
                        sx={{
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Switch
                              checked={edit.go_background}
                              onChange={(e) =>
                                handleTaskGoBackgroundChange(
                                  task.id,
                                  e.target.checked
                                )
                              }
                              color="primary"
                            />
                          }
                          label="Go background"
                        />
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleSaveTask(task.id)}
                        >
                          Save
                        </Button>
                      </Box>
                    </ListItem>
                  );
                })}
              </List>
            </>
          )}
          {createMode && (
            <Box component="form" sx={{ pt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Create a new task
              </Typography>
              <TextField
                autoFocus
                margin="dense"
                label="Task name"
                fullWidth
                variant="standard"
                value={name}
                onChange={handleNameChange}
                error={!!nameError}
                helperText={nameError}
                sx={{ mb: 2 }}
              />
              <TextField
                margin="dense"
                label="Prompt"
                fullWidth
                variant="standard"
                multiline
                minRows={2}
                value={prompt}
                onChange={handlePromptChange}
                error={!!promptError}
                helperText={promptError}
                sx={{ mb: 2 }}
              />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
        <Button onClick={props.onClose}>Close</Button>
        {!createMode && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => setCreateMode(true)}
          >
            Create Task
          </Button>
        )}
        {createMode && (
          <>
            <Button onClick={resetForm}>Cancel</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCreateTask}
            >
              Confirm
            </Button>
          </>
        )}
      </DialogActions>
      <TaskBreakdownModal
        open={!!viewTask}
        project={props.project}
        task={viewTask}
        onClose={() => setViewTask(null)}
      />
    </Dialog>
  );
}
